// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { CartsDataViewContext } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { BackOrderDataViewContext } from "@rc/App/Store/Selectors/BackOrderSelector";
import { pluralize, xScreen } from "@rc/XDL/Utilities";
import React, { FC, useContext, useState } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

type Props = OwnProps;

export interface BackOrdersBackOrderNumberAndPaginationContainerStyles {
    tableBodyStyle?: DataTableBodyProps;
    orderCountText?: TypographyPresentationProps;
}

export const headerStyles: BackOrdersBackOrderNumberAndPaginationContainerStyles = {
    tableBodyStyle: {
        css: css`
            tr {
                td:first-child {
                    &::before {
                        content: "";
                        display: inline-block;
                        width: 12px;
                        height: 12px;
                        border-radius: 100%;
                        margin-right: 6px;
                        position: relative;
                        top: 0;
                        background-color: ${getColor("success.main")};
                    }
                }
            }
        `,
    },
    orderCountText: {
        css: css`
            font-size: 18px;
            font-weight: 500;
            line-height: 24px;
            color: ${getColor("common.ui7")};
        `,
    },
};
const styles = headerStyles;

const BackOrdersBackOrderNumberAndPaginationContainer: FC<Props> = ({ id }) => {
    const backOrdersDataView = useContext(BackOrderDataViewContext);

    if (!backOrdersDataView || !backOrdersDataView.value || !backOrdersDataView.value.length || !backOrdersDataView.pagination) {
        return null;
    }

    const { totalItemCount } = backOrdersDataView.pagination;

    if (totalItemCount === 0) {
        return null;
    }

    return (
        <StyledWrapper
            css={css`
                padding: 13px;
                margin: 32px 0 32px;
                border: 1px solid ${getColor("common.border")};
            `}
        >
            <GridContainer
                css={css`
                    > div {
                        justify-content: space-between;
                    }
                `}
            >
                <GridItem
                    width={[12, 12, 4, 6, 6]}
                    css={css`
                        > div,
                        span {
                            margin: auto 0;
                        }
                        ${xScreen("lte", "sm")} {
                            padding: 25px 12px 10px 12px;
                        }
                    `}
                >
                    <Typography {...styles.orderCountText} data-test-selector="backOrders_resultCount">
                        {pluralize(totalItemCount, translate("Backorder"))}
                    </Typography>
                </GridItem>
                <GridItem
                    width={[12, 12, 7, 6, 6]}
                    css={css`
                        padding: 0 15px 0 0;
                        ${xScreen("gt", "sm")} {
                            padding: 13px;
                        }
                        ${xScreen("lte", "sm")} {
                            padding: 0 12px 20px 12px;
                        }
                    `}
                >
                    <Zone contentId={id} zoneName="TopPaginaton" />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BackOrdersBackOrderNumberAndPaginationContainer,
    definition: {
        group: "XNGAGE" as any,
        displayName: "BackOrder And Pagination Container",
    },
};

export default widgetModule;
